import { Reducer } from '@reduxjs/toolkit'
import { getNearestValidRangeBottom } from 'beam/dist/DataGrid/helpers'
import {
  START_LOADING,
  STOP_LOADING,
  START_DOWNLOADING,
  STOP_DOWNLOADING,
  SET_ACTIVE_FILTERS,
  SET_PAGE_PARAMS,
  CLEAR_ERRORS,
  SAVE_USER_PREFS_COLUMNS_SUCCESS,
  SAVE_USER_PREFS_COLUMNS_FAILURE,
  GET_ALL_COLUMNS_SUCCESS,
  GET_ALL_COLUMNS_FAILURE,
  GET_USER_PREFS_COLUMNS_SUCCESS,
  GET_USER_PREFS_COLUMNS_FAILURE,
  GET_CONTACTS_SUCCESS,
  GET_CONTACTS_FAILURE,
  SET_LAST_SEARCH_ID,
  GET_CONTACTS_STATS_SUCCESS,
  GET_CONTACT_STATS_FAILURE,
  UPDATE_CONTACT_REQUESTED,
  UPDATE_CONTACT_SUCCESS,
  UPDATE_CONTACT_FAILURE,
  GET_MOST_RECENT_SEARCH_SUCCESS,
  GET_MOST_RECENT_SEARCH_FAILURE,
  UPDATE_PAGE_SIZE,
  GET_PAGE_SIZE_FAILURE,
  GET_EVAL_FORMS_SUCCESS,
  GET_EVAL_FORMS_FAILURE,
  GET_RECORDING_TYPES_SUCCESS,
  GET_RECORDING_TYPES_FAILURE,
  GET_TIME_ZONES_SUCCESS,
  GET_TIME_ZONES_FAILURE,
  GET_METADATA_FIELDS_SUCCESS,
  GET_METADATA_FIELDS_FAILURE,
  SAVE_RECORDINGS_COLUMN_WIDTHS,
  GET_RECORDINGS_COLUMN_WIDTHS,
  UPDATE_RECORDINGS_CONTACTS_AFTER_EVALUATION_SAVE,
  UPDATE_RECORDINGS_CONTACTS_AFTER_EVALUATION_RESET,
} from './recordings.constants'
import { CHANGE_FILTER_SET } from './FilterSets/filterSets.constants'
import { composeMostRecentSearchData } from './helpers'
import { RecordingsAction } from './recordings.actions'

interface RecordingsState {
  userPrefsColumns: {
    columnData: $TSFIXME[],
  },
  customColumnWidths: $TSFIXME,
  allColumns: $TSFIXME[],
  contacts: $TSFIXME[],
  contactsStats: $TSFIXME,
  activeFilters: $TSFIXME,
  pageParams: {
    rangeBottom: number,
    rangeTop: number,
  },
  ccrid: $TSFIXME,
  isMediaPlayerOpen: boolean,
  evalForms: $TSFIXME[],
  recordingTypes: $TSFIXME[],
  timeZones: $TSFIXME[],
  metadataFields: $TSFIXME[],
  loading: boolean,
  downloadingFiles: $TSFIXME[],
  errors: $TSFIXME,
  contactSearchPageCursors: $TSFIXME[],
  lastStatsSearchId: string | null,
}

const initialState: RecordingsState = {
  userPrefsColumns: { columnData: [] },
  customColumnWidths: {},
  allColumns: [],
  contacts: [],
  contactsStats: {},
  activeFilters: {},
  pageParams: {
    rangeBottom: 1,
    rangeTop: 80,
  },
  ccrid: null,
  isMediaPlayerOpen: false,
  evalForms: [],
  recordingTypes: [],
  timeZones: [],
  metadataFields: [],
  loading: true,
  downloadingFiles: [],
  errors: null,
  contactSearchPageCursors: [],
  lastStatsSearchId: null,
}

const reducer: Reducer<RecordingsState, RecordingsAction> = (state = initialState, action) => {
  switch (action.type) {
    case START_LOADING:
      return { ...state, loading: true }
    case STOP_LOADING:
      return { ...state, loading: false }
    case START_DOWNLOADING:
      return { ...state, downloadingFiles: state.downloadingFiles.concat([action.itemName]) }
    case STOP_DOWNLOADING:
      return { ...state, downloadingFiles: state.downloadingFiles.slice(1, state.downloadingFiles.length) }
    case SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: composeMostRecentSearchData(action.activeFilters) }
    case SET_PAGE_PARAMS:
      return { ...state, pageParams: action.pageParams }

    case CLEAR_ERRORS:
      return { ...state, errors: null }

    case GET_ALL_COLUMNS_SUCCESS:
      return { ...state, allColumns: action.columns }
    case GET_ALL_COLUMNS_FAILURE:
      return { ...state, allColumns: [], errors: action.error.message }

    case SAVE_USER_PREFS_COLUMNS_SUCCESS:
      return { ...state, userPrefsColumns: action.columns }
    case SAVE_USER_PREFS_COLUMNS_FAILURE:
      return { ...state, userPrefsColumns: { columnData: [] }, errors: action.error.message }

    case GET_USER_PREFS_COLUMNS_SUCCESS:
      return { ...state, userPrefsColumns: action.columns }
    case GET_USER_PREFS_COLUMNS_FAILURE:
      return { ...state, userPrefsColumns: { columnData: [] }, errors: action.error.message }

    case GET_CONTACTS_SUCCESS: {
      let updatedContactSearchPageCursors = [...state.contactSearchPageCursors]
      // being extra safe.. only update the cursor if it is provided.
      // set the cursor for specified page is cursor available; reset all cursors if this is the first page
      if (action.pageCursor?.page) {
        updatedContactSearchPageCursors = action.pageCursor.page === 1 ? [] : [...state.contactSearchPageCursors]
        if (action.pageCursor.nextPageCursor) {
          updatedContactSearchPageCursors[action.pageCursor.page] = action.pageCursor.nextPageCursor
        }
      }
      return { ...state, contacts: action.contacts, contactSearchPageCursors: updatedContactSearchPageCursors }
    }
    case GET_CONTACTS_FAILURE:
      return { ...state, contacts: [], errors: action.error.message, contactSearchPageCursors: [] } // reset the search page cursors on failure

    case SET_LAST_SEARCH_ID:
      return { ...state, lastStatsSearchId: action.id, contactsStats: {} }

    case GET_CONTACTS_STATS_SUCCESS: {
      // Only set stats if the search id matches the last search id.
      // Otherwise, we might overwrite a stats call that has happenned since this one was initiated.
      if (!state.lastStatsSearchId || action.id === state.lastStatsSearchId) {
        return { ...state, contactsStats: { ...action.stats, error: false } }
      } else {
        return state
      }
    }

    case GET_CONTACT_STATS_FAILURE: {
      // Only log a failure and reset things if this is the most recent stats query.
      if (!state.lastStatsSearchId || action.id === state.lastStatsSearchId) {
        return { ...state, contactsStats: { error: true }, errors: action.error.message, contactSearchPageCursors: [] }  // reset the search page cursors on failure
      } else {
        return state
      }
    }

    case UPDATE_CONTACT_REQUESTED:
      return state
    case UPDATE_CONTACT_SUCCESS:
      return {
        ...state,
        contacts: state.contacts.map(contact => (contact.id === action.contact.id ?
          { ...action.contact, textSearchPreview: contact.textSearchPreview } :
          contact)),
      }
    case UPDATE_CONTACT_FAILURE:
      return { ...state, errors: action.error.message }

    case UPDATE_RECORDINGS_CONTACTS_AFTER_EVALUATION_SAVE:
      return {
        ...state,
        contacts: state.contacts.map(contact =>
          contact.id === action.contactId
            ? {
              ...contact,
              evalStateId: action.payload.state.stateId,
              evalForm: {
                ...contact.evalForm,
                ...action.payload.evalForm,
              },
              evaluator: {
                ...contact.evaluator,
                ...action.payload.evaluator,
              },
              approver:
                  action.payload.approver === null
                    ? {
                      $ref: '/api/rest/recording/person/0',
                      firstName: null,
                      lastName: null,
                      displayId: '0',
                      username: null,
                    }
                    : action.payload.approver,
              evaluation: {
                ...contact.evaluation,
                $ref: `/api/rest/recording/contact/${action.contactId}/eval/${action.payload.id}`,
                id: action.payload.id,
                stateId: action.payload.state.stateId,
                totalScore: action.payload.totalScore,
                evaluated: action.payload.evaluated,
                evaluatedTz: action.payload.evaluatedTz,
                responseDate: action.payload.responseDate,
                responseState: action.payload.responseState,
              },
            }
            : contact,
        ),
      }

    case UPDATE_RECORDINGS_CONTACTS_AFTER_EVALUATION_RESET:
      return {
        ...state,
        contacts: state.contacts.map(contact =>
          contact.id === action.contactId
            ? {
              ...contact,
              evalStateId: 0,
              evaluator: {
                $ref: '/api/rest/recording/person/0',
                firstName: null,
                lastName: null,
                displayId: '0',
                username: null,
              },
              approver: {
                $ref: '/api/rest/recording/person/0',
                firstName: null,
                lastName: null,
                displayId: '0',
                username: null,
              },
              evaluation: null,
            }
            : contact,
        ),
      }

    case GET_MOST_RECENT_SEARCH_SUCCESS:
      return { ...state, activeFilters: action.data }
    case GET_MOST_RECENT_SEARCH_FAILURE:
      return { ...state, errors: action.error.message }

    case UPDATE_PAGE_SIZE: {
      const rangeBottom = getNearestValidRangeBottom(state.pageParams.rangeBottom, action.data.pageSize)

      return {
        ...state,
        pageParams: {
          ...state.pageParams,
          rangeBottom,
          rangeTop: rangeBottom + action.data.pageSize - 1,
        },
      }
    }
    case GET_PAGE_SIZE_FAILURE:
      return { ...state, errors: action.error.message }

    case GET_EVAL_FORMS_SUCCESS:
      return { ...state, evalForms: action.evalForms }
    case GET_EVAL_FORMS_FAILURE:
      return { ...state, errors: action.error.message }

    case GET_RECORDING_TYPES_SUCCESS:
      return { ...state, recordingTypes: action.recordingTypes }
    case GET_RECORDING_TYPES_FAILURE:
      return { ...state, errors: action.error.message }

    case GET_TIME_ZONES_SUCCESS:
      return { ...state, timeZones: action.timeZones }
    case GET_TIME_ZONES_FAILURE:
      return { ...state, errors: action.error.message }

    case GET_METADATA_FIELDS_SUCCESS:
      return { ...state, metadataFields: action.data }
    case GET_METADATA_FIELDS_FAILURE:
      return { ...state, errors: action.error.message }
    case SAVE_RECORDINGS_COLUMN_WIDTHS:
      return { ...state, customColumnWidths: action.recordingsColumnWidths }
    case GET_RECORDINGS_COLUMN_WIDTHS:
      return { ...state, customColumnWidths: action.recordingsColumnWidths }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    case CHANGE_FILTER_SET as $TSFIXME: // TODO: move this to its corresponding reducer
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
      return { ...state, activeFilters: (action as $TSFIXME).filters }
    default:
      return state
  }
}

export default reducer
